import type { CSSProperties, HTMLAttributes, ReactElement, ReactNode } from "react";

export type ProductCardLayout = "overlay" | "catalogue";

export const PRODUCT_CARD_ASPECT_RATIO = 3 / 5;

export type ProductCardSlotClasses = Partial<{
  root: string;
  blurImage: string;
  sharpImage: string;
}>;

export type ProductCardViewProps = {
  image: string;
  title: string;
  price?: string;
  attribution?: string;
  alt?: string;
  featured?: boolean;
  layout?: ProductCardLayout;
  imageScale?: number;
  imageX?: number;
  imageY?: number;
  objectPosition?: string;
  imageMode?: "single" | "focus";
  className?: string;
  style?: CSSProperties;
  mediaProps?: HTMLAttributes<HTMLDivElement>;
  mediaOverlay?: ReactNode;
  placeholderIcon?: string;
  slotClasses?: ProductCardSlotClasses;
  as?: "article" | "div";
};

function classNames(...values: Array<string | undefined | false>): string {
  return values.filter(Boolean).join(" ");
}

export function ProductCardView({
  image,
  title,
  price,
  attribution,
  alt = "",
  featured = false,
  layout = "catalogue",
  imageScale = 1,
  imageX = 0,
  imageY = 0,
  objectPosition = "50% 50%",
  imageMode = "single",
  className,
  style,
  mediaProps,
  mediaOverlay,
  placeholderIcon,
  slotClasses = {},
  as = "article",
}: ProductCardViewProps): ReactElement {
  const Root = as;
  const mediaClassName = classNames("hfp-card-media", !image && "hfp-card-media-empty", mediaProps?.className);
  const transform = { transform: `translate(${imageX}%,${imageY}%) scale(${imageScale})` };
  const coverStyle = { backgroundImage: `url(${JSON.stringify(image)})`, backgroundPosition: objectPosition };
  const imageElement = image
    ? imageMode === "focus"
      ? <><img className="hfp-card-image-loader" draggable="false" src={image} alt={alt}/><span aria-hidden="true" className={classNames("hfp-card-image", "hfp-card-image-blur", slotClasses.blurImage)} style={coverStyle}/><span aria-hidden="true" className={classNames("hfp-card-image", "hfp-card-image-sharp", slotClasses.sharpImage)} style={coverStyle}/></>
      : <><img className="hfp-card-image-loader" draggable="false" src={image} alt={alt}/><span aria-hidden="true" className="hfp-card-image" style={coverStyle}/></>
    : <div className="hfp-card-placeholder">{placeholderIcon ? <img src={placeholderIcon} alt=""/> : null}<span>Add an image</span></div>;

  return <Root className={classNames("hfp-card", `hfp-card-${layout}`, slotClasses.root, className)} data-card-layout={layout} style={style}>
    <div {...mediaProps} className={mediaClassName}>
      <div className="hfp-card-image-transform" style={transform}>{imageElement}</div>
      <span className="hfp-card-wash"/>
      {featured ? <span className="hfp-card-badge">Featured</span> : null}
      {mediaOverlay}
    </div>
    <div className="hfp-card-copy">
      {title ? <h3>{title}</h3> : null}
      <div className="hfp-card-meta">{price ? <strong>{price}</strong> : null}{attribution ? <small>{attribution}</small> : null}</div>
    </div>
  </Root>;
}

export function productCardCss(scope = ""): string {
  const s = scope ? `${scope} ` : "";
  return `${s}.hfp-card{position:relative;width:100%;height:100%;aspect-ratio:${PRODUCT_CARD_ASPECT_RATIO};overflow:hidden;container-type:inline-size;border:1px solid rgba(23,32,51,.08);border-radius:var(--hfp-card-radius,22px);background:#fff;color:#172033;box-shadow:0 5cqw 13cqw rgba(36,44,60,.1)}
${s}.hfp-card-media{position:absolute;z-index:1;inset:0;overflow:hidden;background:#e8e4df}
${s}.hfp-card-image-transform,${s}.hfp-card-image,${s}.hfp-card-wash{position:absolute;inset:0;width:100%;height:100%}
${s}.hfp-card-image-transform{transform-origin:50% 50%;will-change:transform}
${s}.hfp-card-image-loader{position:absolute;width:1px;height:1px;opacity:0;pointer-events:none}
${s}.hfp-card-image{background-position:50% 50%;background-repeat:no-repeat;background-size:cover;user-select:none}
${s}.hfp-card-image-blur{filter:blur(var(--fc-blur,0));transform:scale(1.06)}
${s}.hfp-card-image-sharp{opacity:0}
${s}.hfp-card-wash{z-index:1;background:linear-gradient(180deg,transparent 46%,rgba(5,8,13,.18) 62%,rgba(5,8,13,.94) 100%);pointer-events:none}
${s}.hfp-card-badge{position:absolute;z-index:2;left:6cqw;top:5.5cqw;padding:2.7cqw 4.2cqw;border-radius:999px;background:rgba(255,255,255,.92);color:#172033;font:850 3.4cqw/1 var(--font-body,Inter,sans-serif);letter-spacing:.07em;text-transform:uppercase}
${s}.hfp-card-copy{position:absolute;z-index:2;color:#172033;font-family:var(--font-body,Inter,sans-serif)}
${s}.hfp-card-copy h3{margin:0;font:800 5.2cqw/1.18 var(--font-display,Inter,sans-serif);letter-spacing:-.025em}
${s}.hfp-card-meta{display:flex;align-items:end;justify-content:space-between;gap:4cqw}
${s}.hfp-card-meta strong{font:800 4.8cqw/1 var(--font-body,Inter,sans-serif)}
${s}.hfp-card-meta small{color:#8b929e;font:600 3.35cqw/1 var(--font-body,Inter,sans-serif);white-space:nowrap}
${s}.hfp-card-placeholder{display:grid;place-items:center;width:100%;height:100%;color:#8b9099;font:750 4cqw/1 var(--font-body,Inter,sans-serif)}
${s}.hfp-card-catalogue .hfp-card-media{bottom:14%;height:auto}
${s}.hfp-card-catalogue .hfp-card-wash{display:none}
${s}.hfp-card-catalogue .hfp-card-copy{left:0;right:0;bottom:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1.5cqw;height:14%;padding:3cqw 5cqw;background:#fff;text-align:center}
${s}.hfp-card-catalogue .hfp-card-meta{align-self:auto;justify-content:center}
${s}.hfp-card-overlay{border-color:rgba(255,255,255,.18);background:#111722;color:#fff}
${s}.hfp-card-overlay .hfp-card-copy{left:0;right:0;bottom:0;padding:9cqw 7cqw 7cqw;color:#fff;text-align:center;text-shadow:0 .5cqw 2.4cqw rgba(0,0,0,.52)}
${s}.hfp-card-overlay .hfp-card-copy h3{color:#fff;font-size:5.5cqw;line-height:1.05}
${s}.hfp-card-overlay .hfp-card-meta{justify-content:center;flex-direction:column;align-items:center;gap:2.8cqw;margin-top:3.5cqw}
${s}.hfp-card-overlay .hfp-card-meta strong{color:#fff}
${s}.hfp-card-overlay .hfp-card-meta small{color:rgba(255,255,255,.62);font-size:2.8cqw;font-weight:700;letter-spacing:.06em;text-transform:uppercase}`;
}
